
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  validateSignupForm, 
  createThrottleManager, 
  logSignupSuccess, 
  logSignupError 
} from './SignupValidation';
import { logger } from '@/lib/logger';

export const useSignupForm = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isThrottled, setIsThrottled] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  
  const throttleSignupAttempts = createThrottleManager(setIsThrottled);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Check if throttled
    if (isThrottled) {
      setError("Too many signup attempts. Please try again in 30 seconds.");
      return;
    }

    // Validate form inputs
    const validation = validateSignupForm(email, password, confirmPassword, fullName);
    if (!validation.isValid) {
      setError(validation.errorMessage);
      return;
    }

    setLoading(true);

    try {
      // Apply throttling
      throttleSignupAttempts();
      
      // Use Supabase's built-in email verification
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/login`
        },
      });

      if (error) throw error;
      
      // Log successful signup for analytics
      logSignupSuccess(email);
      
      setSignupSuccess(true);
      toast({
        title: "Account created",
        description: "Check your email for the verification link.",
      });
      
    } catch (error: any) {
      setError(error.message);
      logSignupError(error, email);
      
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setResendLoading(true);
    setError(null);
    
    try {
      // Use Supabase's built-in resend verification
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      
      if (error) throw error;
      
      toast({
        title: "Verification email resent",
        description: "Please check your inbox for the verification link.",
      });
    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    fullName,
    setFullName,
    loading,
    resendLoading,
    error,
    isThrottled,
    signupSuccess,
    handleSignUp,
    handleResendEmail
  };
};
